import React, {CSSProperties} from "react";
import cx from 'clsx';
import {Button, ButtonStyles} from "../button";
import {LinkHijack} from "@montana/helpers/link-hijack.helper";
import {isBrowser} from "@montana/helpers/browser.helper";
import {staticSettings} from "@montana/helpers/static-settings.helper";
import {Appear} from "../appear";
import {useAuthentication} from "@montana/containers/authentication";
import {formatRichTextLinks} from "@montana/helpers/rich-text.helper";
import {TEXT_DARK, TEXT_LIGHT} from '@montana/constants/colors';
import {useDynamicSettings} from "@montana/containers/dynamic-settings";

enum ButtonLinkSelectors {
    Primary = "[data-epi='primary'],[data-epi='tertiary']",
    PrimaryWhite = "[data-epi='primary-white']",
    PrimaryBlack = "[data-epi='primary-black']",
    Secondary = "[data-epi='secondary']",
    SecondaryWhite = "[data-epi='secondary-white']",
    Text = "[data-epi='text'],[data-epi='arrow']",
}

interface IRichTextProps {
    text: string;
    className?: string;
    disableAppear?: boolean;
    appearDelay?: number;
    translation?: boolean; // If true all RTE default styling is disabled.
    marginOnFirstChild?: boolean; // If true margins will also be added to first child of RTE.
    marginOnLastChild?: boolean; // If true margins will also be added to last child of RTE.
}

enum textReplaces {
    EMAIL = "{user-email}",
}

// Patterns used in rich text fields in EpiServer
const colorMatchPattern = new RegExp(/##NumberOfColors##/gi);
const colorLaquersMatchPattern = new RegExp(/##NumberOfLaquersColors##/gi);

export const RichText: React.FC<IRichTextProps> = React.memo((props: IRichTextProps): JSX.Element | null => {
    const {
        text,
        className,
        disableAppear = false,
        appearDelay,
        translation = false,
        marginOnFirstChild,
        marginOnLastChild
    } = props;

    if (!text) {
        return null;
    }

    let textContent = text;

    const {user} = useAuthentication();
    const {numberOfColors, numberOfLaquersColors, language, languages} = useDynamicSettings();

    // Replace instances of ##NumberOfColors## or ##NumberOfLaquersColors## in the rich text content, with number of colors provided through the DynamicSettings.
    // The values are defined in EpiServer on the Home page item under the tab "Colors".
    const colorNumberMatch = text.match(colorMatchPattern);
    const colorLaquersNumberMatch = text.match(colorLaquersMatchPattern);

    if (!!colorNumberMatch) {
        textContent = textContent.replace(colorMatchPattern, numberOfColors.toString());
    }

    if (!!colorLaquersNumberMatch) {
        textContent = textContent.replace(colorLaquersMatchPattern, numberOfLaquersColors.toString());
    }

    // Replace instances of email with user email
    textContent = textContent.replace(textReplaces.EMAIL, user?.email || '{mail-missing}');
    textContent = text && language ? formatRichTextLinks(textContent, language, languages) : textContent;

    // Make relative image sources absolute with bff url
    const srcRegex = /(src=")(?!https?:\/\/)\/?/ig;
    const url = staticSettings.apiUrl.endsWith("/") ? staticSettings.apiUrl : `${staticSettings.apiUrl}/`;
    textContent = textContent.replace(srcRegex, `src="${url}`);

    // Make relative href urls lowercase
    const linkRegex = /(?:href=")(?!http)([^"]*)/gi;
    textContent = textContent.replace(linkRegex, match => match.toLowerCase());

    const cssClassName = cx('rich-text', (className ? className?.trim() : ''), {
        'rich-text--translation': translation,
        'rich-text--margin-on-first-child': !translation && marginOnFirstChild,
        'rich-text--margin-on-last-child': !translation && marginOnLastChild,
    });

    if (!isBrowser) {
        return (
            <div
                className={cssClassName}
                dangerouslySetInnerHTML={{__html: textContent}}
            />
        )
    } else {
        const frag = document.createRange().createContextualFragment(textContent);

        return (
            <div className={cssClassName}>
                {Array.from(frag.children).map((node, index) => {
                    const element = node as HTMLElement;
                    const buttonLinks = [
                        {
                            style: ButtonStyles.Primary,
                            links: element.querySelectorAll(ButtonLinkSelectors.Primary),
                        },
                        {
                            style: ButtonStyles.Primary,
                            color: TEXT_LIGHT,
                            links: element.querySelectorAll(ButtonLinkSelectors.PrimaryWhite),
                        },
                        {
                            style: ButtonStyles.Primary,
                            color: TEXT_DARK,
                            links: element.querySelectorAll(ButtonLinkSelectors.PrimaryBlack),
                        },
                        {
                            style: ButtonStyles.Secondary,
                            links: element.querySelectorAll(ButtonLinkSelectors.Secondary),
                        },
                        {
                            style: ButtonStyles.Secondary,
                            color: TEXT_LIGHT,
                            links: element.querySelectorAll(ButtonLinkSelectors.SecondaryWhite),
                        },
                        {
                            style: ButtonStyles.Text,
                            links: element.querySelectorAll(ButtonLinkSelectors.Text),
                        },
                    ];

                    // Remove links before render
                    buttonLinks.forEach(buttonLink => {
                        Array.from(buttonLink.links).forEach(link => {
                            if (link) {
                                if (link.parentNode) {
                                    link.parentNode.removeChild(link);
                                }
                            }
                        });
                    });

                    const style = {
                        textAlign: element.style.textAlign,
                    } as CSSProperties;

                    // Render the content of paragraph tag first, then render the button.
                    // That means you can't have button inline, it will always render as a seperate tag, after the original paragraph
                    return (
                        <React.Fragment key={index}>
                            <LinkHijack>
                                {({onClick}): JSX.Element => (
                                    <Appear
                                        disable={disableAppear}
                                        delay={appearDelay}
                                        dangerouslySetInnerHTML={{__html: node.outerHTML}}
                                        onClick={onClick}
                                    />
                                )}
                            </LinkHijack>
                            {
                                buttonLinks.map((buttonLink, i) => (
                                    (buttonLink.links.length) ? (
                                            <Appear disable={disableAppear} delay={appearDelay} key={i} style={style}>
                                                {Array.from(buttonLink.links).map((link, index) => {
                                                    const anchorElement = link as HTMLAnchorElement;

                                                    return (
                                                        <Button
                                                            key={index}
                                                            className="rich-text__button"
                                                            style={buttonLink.style}
                                                            href={anchorElement.getAttribute('href') || undefined}
                                                            title={anchorElement.title}
                                                            target={anchorElement.target}
                                                            color={buttonLink.color}
                                                        >
                                                            {anchorElement.innerText}
                                                        </Button>
                                                    );
                                                })}
                                            </Appear>
                                        )
                                        : ""
                                ))
                            }
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }
});
